import AbstractApiModel from '@/models/abstracts/AbstractApiModel';
import doc from '@model-core/decorators/doc';
import type { Dayjs } from 'dayjs';

import CompanyModel from '@/models/entities/CompanyModel';
import UserModel from '@/models/entities/UserModel';
import { referenceOf } from '@/stores/useReferenceOptions';
import { urnOf } from '@/models/urn';
import { DEFAULT_TIMEZONE } from '@/lib/companyTime';

/** URN pour écrire une relation vers cette ressource (corps d'un POST / PATCH) ; un filtre prend l'id */
export const memberUrn = (memberId: id | number) => urnOf('member', memberId);

/**
 * Rattachement d'un user à une société avec son rôle dans la société (GET /members).
 * companyRole arrive en {id, reference, label, position} ; user et company réduits à leur identité : à résoudre par
 * l'écran (fetchResolvable('user'), fetchResolvable('company')) pour leur nom.
 * Admin : tous les rattachements ; utilisateur : ceux de ses sociétés (contexte action-control company).
 * Écriture (Dirigeant de la société) : POST {user, company, companyRole} (IRI), DELETE ; user et company ne changent plus ensuite.
 */
export default class MemberModel extends AbstractApiModel {
  protected _path = '/members/{?id}';

  @doc.model(() => UserModel) declare user: UserModel;
  @doc.model(() => CompanyModel) declare company: CompanyModel;

  declare _tsFilters: { user?: string; company?: string; companyRole?: id };
  declare _tsSorts: { 'user.lastName'?: unknown; 'company.name'?: unknown };

  /** Date du rattachement */
  @doc.date declare createdAt: Dayjs;
  /** Auteur de la création : URN de la personne connectée ($:teiji:user:12), vide pour les fixtures, migrations, imports */
  @doc.string declare createdBy: string;
  @doc.date declare updatedAt: Dayjs;
  /** Auteur de la dernière modification (URN) (changement de rôle) */
  @doc.string declare updatedBy: string;

  get userId(): number {
    return Number(this.user.id) || 0;
  }

  get firstName(): string {
    return this.user.firstName;
  }

  get lastName(): string {
    return this.user.lastName;
  }

  get email(): string {
    return this.user.email;
  }

  /** NOM Prénom : convention du projet, le nom toujours avant le prénom */
  get fullName(): string {
    return `${this.lastName} ${this.firstName}`.trim();
  }

  get companyId(): number {
    return Number(this.company.id) || 0;
  }

  get companyName(): string {
    return this.company.name;
  }

  get companyTimezone(): string {
    return this.company.timezone || DEFAULT_TIMEZONE;
  }

  /** DIRECTOR / ASSISTANT / EMPLOYEE */
  get companyRoleReference(): string {
    return referenceOf('companyRoles', this.get<unknown, unknown>('companyRole', null))?.reference ?? '';
  }

  get companyRoleLabel(): string {
    return referenceOf('companyRoles', this.get<unknown, unknown>('companyRole', null))?.label ?? '';
  }

  /** Ordre du rôle dans la société (Dirigeant 0, Assistant 1, Employé 2) ; inconnu : en dernier */
  get companyRolePosition(): number {
    return (
      referenceOf('companyRoles', this.get<unknown, unknown>('companyRole', null))?.position ?? Number.MAX_SAFE_INTEGER
    );
  }
}
